import { useState, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Download, History, Loader2, AlertTriangle, Check, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface CorridorStopEntry {
  stop_id: string;
  stop_name: string;
  position: number;
  is_off_route: boolean;
  is_draft: boolean;
}

interface Corridor {
  id: string;
  name: string;
  stops: CorridorStopEntry[];
}

interface TransitStop {
  id: string;
  name: string;
}

interface FareEntry {
  id: string;
  origin: string;
  destination: string;
  fare_xcd: number;
  notes: string | null;
  is_active: boolean;
  is_draft: boolean;
  updated_at: string | null;
}

interface FareMatrixProps {
  fares: FareEntry[];
  corridors: Corridor[];
  stops: TransitStop[];
  onRefresh: () => void;
}

export default function FareMatrix({ fares, corridors, stops, onRefresh }: FareMatrixProps) {
  const [selectedCorridorId, setSelectedCorridorId] = useState<string>(corridors[0]?.id || '');
  const [editingCell, setEditingCell] = useState<{
    fromId: string;
    toId: string;
    fromName: string;
    toName: string;
  } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCell, setHistoryCell] = useState<{ from: string; to: string } | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Get corridor stops in order from the corridor's embedded stops array
  const selectedCorridor = corridors.find(c => c.id === selectedCorridorId);
  const corridorStops = useMemo(() => {
    if (!selectedCorridor?.stops) return [];
    return selectedCorridor.stops
      .sort((a, b) => a.position - b.position)
      .map(cs => {
        const stop = stops.find(s => s.id === cs.stop_id);
        return stop || { id: cs.stop_id, name: cs.stop_name };
      });
  }, [selectedCorridor, stops]);

  /*
   * Look up the fare for a pair BY STOP NAME.
   *
   * `corridor_fares.origin/destination` hold stop names ('Roseau'), not
   * stop UUIDs. This used to compare against `stop.id`, so no row ever
   * matched: every cell rendered "—" even for the 80-odd published West
   * Coast fares, and saving wrote a UUID into the name column, creating
   * rows the mobile app could never find.
   *
   * Matched in either direction because a government fare covers the
   * journey both ways, and the app's own lookup is bidirectional. The
   * table therefore holds ONE row per journey, shown in both triangles.
   */
  const getFare = (fromName: string, toName: string): FareEntry | undefined => {
    return fares.find(
      f =>
        (f.origin === fromName && f.destination === toName) ||
        (f.origin === toName && f.destination === fromName),
    );
  };

  // Start editing
  const startEdit = (from: { id: string; name: string }, to: { id: string; name: string }) => {
    const fare = getFare(from.name, to.name);
    setEditingCell({ fromId: from.id, toId: to.id, fromName: from.name, toName: to.name });
    setEditValue(fare ? String(fare.fare_xcd) : '');
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  // Save fare using admin_upsert_fare
  const handleSaveFare = async () => {
    if (!editingCell) return;
    const value = parseFloat(editValue);
    if (isNaN(value) || value < 0) {
      toast.error('Invalid fare amount');
      return;
    }
    setSaving(true);
    try {
      /*
       * Update the existing row in place when the journey is already
       * priced in either direction. Sending names alone would upsert on
       * (origin, destination) and mirror 'Layou → Jimmit' alongside
       * 'Jimmit → Layou' — two rows for one regulated journey, free to
       * drift to different prices.
       */
      const existing = getFare(editingCell.fromName, editingCell.toName);
      const { data: fareData, error } = await supabase.rpc('admin_upsert_fare', {
        p_id: existing?.id ?? null,
        p_origin: existing?.origin ?? editingCell.fromName,
        p_destination: existing?.destination ?? editingCell.toName,
        p_fare_xcd: value,
        p_is_draft: true,
      });
      if (error) throw error;
      const fareResult = fareData as any;
      if (fareResult?.ok === false) throw new Error(fareResult.error || 'Failed to save fare');
      toast.success(`Fare set to EC$${value.toFixed(2)} (draft)`);
      setEditingCell(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save fare');
    } finally {
      setSaving(false);
    }
  };

  // Publish fares using admin_publish_drafts
  const handlePublish = async () => {
    setPublishing(true);
    try {
      const { data: pubData, error } = await supabase.rpc('admin_publish_drafts', { p_corridor_id: selectedCorridorId });
      if (error) throw error;
      const pubResult = pubData as any;
      if (pubResult?.ok === false) throw new Error(pubResult.error || 'Publish failed');
      // Report the count rather than a blanket success. This button used
      // to claim it had published fares while publishing none, and the
      // only hint was the draft badge refusing to clear.
      const published = Number(pubResult?.fares_published ?? 0);
      if (published > 0) {
        toast.success(
          `${published} fare${published > 1 ? 's' : ''} published. Mobile apps will sync within seconds.`,
        );
      } else {
        toast.error('No fares were published. Reload and try again, or check your admin access.');
      }
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish fares');
    } finally {
      setPublishing(false);
    }
  };

  // Open history dialog (no RPC available — show empty state)
  const openHistory = (fromId: string, toId: string) => {
    setHistoryCell({ from: fromId, to: toId });
    setHistoryOpen(true);
  };

  // CSV export
  const handleExport = () => {
    if (corridorStops.length === 0) return;
    const headers = ['From', 'To', 'Fare (EC$)', 'Status'];
    const rows: string[][] = [];
    corridorStops.forEach(from => {
      corridorStops.forEach(to => {
        if (from.id === to.id) return;
        const fare = getFare(from.name, to.name);
        rows.push([
          from.name,
          to.name,
          fare ? fare.fare_xcd.toFixed(2) : '',
          fare ? (fare.is_draft ? 'Draft' : 'Published') : 'Not set',
        ]);
      });
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fares-${selectedCorridorId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const draftCount = fares.filter(f => f.is_draft).length;
  const fromStop = historyCell ? stops.find(s => s.id === historyCell.from) : null;
  const toStop = historyCell ? stops.find(s => s.id === historyCell.to) : null;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Regulatory banner */}
      <div className="px-4 py-2.5 bg-amber-900/20 border-b border-amber-700/30 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
        <p className="text-xs text-amber-300">
          Fares are regulated by the Government of Dominica. Changes must be approved before publishing.
          Draft changes are visible only to admins until published.
        </p>
      </div>

      {/* Toolbar */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3">
        <Select value={selectedCorridorId} onValueChange={setSelectedCorridorId}>
          <SelectTrigger className="w-[200px] bg-gray-800 border-gray-700 text-white">
            <SelectValue placeholder="Select corridor" />
          </SelectTrigger>
          <SelectContent>
            {corridors.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        {draftCount > 0 && (
          <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">
            {draftCount} draft fare{draftCount > 1 ? 's' : ''}
          </Badge>
        )}

        <Button
          size="sm"
          variant="outline"
          onClick={handleExport}
          disabled={corridorStops.length === 0}
          className="border-gray-600 text-gray-300 hover:text-white h-8"
        >
          <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
        </Button>

        <Button
          size="sm"
          onClick={handlePublish}
          disabled={publishing || draftCount === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
        >
          {publishing && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
          Publish fares
        </Button>
      </div>

      {/* Matrix table */}
      <div className="flex-1 overflow-auto p-4">
        {corridorStops.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-gray-500">
            <p>No stops in this corridor. Add stops in the Corridor Builder tab first.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-gray-900 z-10 p-2 text-left text-xs text-gray-500 font-medium border-b border-gray-800 min-w-[120px]">
                    From ↓ / To →
                  </th>
                  {corridorStops.map(to => (
                    <th key={to.id} className="p-2 text-center text-xs text-gray-400 font-medium border-b border-gray-800 min-w-[80px] whitespace-nowrap">
                      {to.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {corridorStops.map(from => (
                  <tr key={from.id}>
                    <td className="sticky left-0 bg-gray-900 z-10 p-2 text-xs text-gray-300 font-medium border-b border-gray-800/50 whitespace-nowrap">
                      {from.name}
                    </td>
                    {corridorStops.map(to => {
                      if (from.id === to.id) {
                        return (
                          <td key={to.id} className="p-2 text-center border-b border-gray-800/50 bg-gray-800/30">
                            <span className="text-gray-600">–</span>
                          </td>
                        );
                      }
                      const fare = getFare(from.name, to.name);
                      const isEditing = editingCell?.fromId === from.id && editingCell?.toId === to.id;

                      return (
                        <td key={to.id} className="p-1 text-center border-b border-gray-800/50 relative group">
                          {isEditing ? (
                            <div className="flex items-center gap-0.5">
                              <Input
                                ref={editInputRef}
                                type="number"
                                step="0.50"
                                min="0"
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                className="w-16 h-7 text-xs bg-gray-800 border-blue-500 text-white text-center p-1"
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveFare();
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                              />
                              <button onClick={handleSaveFare} disabled={saving} className="text-emerald-400 hover:text-emerald-300">
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => setEditingCell(null)} className="text-gray-500 hover:text-gray-300">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="cursor-pointer px-2 py-1 rounded hover:bg-gray-700/50 transition-colors inline-flex items-center gap-1"
                              onClick={() => startEdit(from, to)}
                            >
                              {fare ? (
                                <>
                                  <span className={`font-mono text-xs ${fare.is_draft ? 'text-blue-400' : 'text-gray-200'}`}>
                                    {fare.fare_xcd.toFixed(2)}
                                  </span>
                                  {fare.is_draft && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
                                </>
                              ) : (
                                <span className="text-gray-600 text-xs">–</span>
                              )}
                              {/* History button on hover */}
                              <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 ml-0.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openHistory(from.id, to.id); }}
                                  className="text-gray-500 hover:text-gray-300"
                                  title="View history"
                                >
                                  <History className="w-3 h-3" />
                                </button>
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History drawer — no RPC available, show empty state */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Fare history</DialogTitle>
            <DialogDescription className="text-gray-400">
              {fromStop?.name} → {toStop?.name}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-500 text-center py-6">
            Fare history is not yet available. Changes are tracked in the audit log.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
